/**
 * Phone Number Utility
 * Handles parsing and formatting of international phone numbers
 * Uses libphonenumber-js for accurate parsing across all platforms
 */

import { parsePhoneNumber, CountryCode, getCountryCallingCode } from 'libphonenumber-js';

export interface ParsedPhoneNumber {
  isValid: boolean;
  countryCode: string;      // e.g., '+20', '+966'
  nationalNumber: string;   // e.g., '1234567890'
  country: string;          // e.g., 'EG', 'SA'
  internationalFormat: string; // e.g., '+20 123 456 7890'
  e164Format: string;       // e.g., '+201234567890'
}

/**
 * Parse international phone number into components
 * Handles country code conflicts correctly (e.g., +20 vs +201, +965 vs +966)
 */
export const parseInternationalPhoneNumber = (phoneNumber: string): ParsedPhoneNumber | null => {
  try {
    console.log('📞 [PhoneUtil] Parsing phone number:', phoneNumber);
    
    // Clean the phone number
    let cleanedPhone = phoneNumber.trim();
    
    if (!cleanedPhone) {
      console.log('📞 [PhoneUtil] Empty phone number');
      return null;
    }
    
    // Ensure phone number has + prefix for proper parsing
    // This is critical for numbers without + (e.g., CSV imports)
    if (!cleanedPhone.startsWith('+')) {
      cleanedPhone = `+${cleanedPhone}`;
    }
    
    // Try to parse the phone number
    let parsed;
    try {
      parsed = parsePhoneNumber(cleanedPhone);
    } catch (parseError) {
      console.log('📞 [PhoneUtil] parsePhoneNumber threw error, trying manual country detection:', parseError);
      // Try manual country detection from dial code
      return detectCountryFromDialCode(cleanedPhone);
    }
    
    if (!parsed) {
      console.log('📞 [PhoneUtil] parsePhoneNumber returned null, trying manual country detection');
      return detectCountryFromDialCode(cleanedPhone);
    }
    
    // Even if validation fails, try to extract country info if available
    // This helps with numbers like 9661023356631 where format might not be perfect
    // but country code is detectable
    const hasCountry = !!parsed.country;
    const isValidFormat = parsed.isValid();
    
    if (hasCountry) {
      // If format is not valid but we have a country, still return the parsed info
      // This handles edge cases where number format is slightly off but country is clear
      const result: ParsedPhoneNumber = {
        isValid: isValidFormat,
        countryCode: `+${parsed.countryCallingCode}`,
        nationalNumber: parsed.nationalNumber,
        country: parsed.country || '',
        internationalFormat: parsed.formatInternational(),
        e164Format: parsed.format('E.164')
      };
      
      console.log('📞 [PhoneUtil] Successfully parsed:', result);
      return result;
    }
    
    // No country detected from parsing, try manual detection
    console.log('📞 [PhoneUtil] No country detected from parsing, trying manual detection');
    return detectCountryFromDialCode(cleanedPhone);
    
  } catch (error) {
    console.error('📞 [PhoneUtil] Error parsing phone number:', error);
    return null;
  }
};

/**
 * Manual country detection from dial code when parsing fails
 * Handles cases like 9661023356631 where format might not match expected patterns
 */
function detectCountryFromDialCode(phoneNumber: string): ParsedPhoneNumber | null {
  try {
    // Remove + and extract digits
    const digitsOnly = phoneNumber.replace(/\D/g, '');
    
    if (digitsOnly.length < 7) {
      return null;
    }
    
    // Common country dial codes (longest first to avoid conflicts like +20 vs +201)
    const dialCodes: Array<{ code: string; country: string; length: number }> = [
      { code: '966', country: 'SA', length: 3 }, // Saudi Arabia
      { code: '971', country: 'AE', length: 3 }, // UAE
      { code: '965', country: 'KW', length: 3 }, // Kuwait
      { code: '974', country: 'QA', length: 3 }, // Qatar
      { code: '973', country: 'BH', length: 3 }, // Bahrain
      { code: '968', country: 'OM', length: 3 }, // Oman
      { code: '961', country: 'LB', length: 3 }, // Lebanon
      { code: '962', country: 'JO', length: 3 }, // Jordan
      { code: '964', country: 'IQ', length: 3 }, // Iraq
      { code: '20', country: 'EG', length: 2 },  // Egypt
      { code: '212', country: 'MA', length: 3 }, // Morocco
      { code: '213', country: 'DZ', length: 3 }, // Algeria
      { code: '216', country: 'TN', length: 3 }, // Tunisia
      { code: '1', country: 'US', length: 1 },   // USA/Canada (needs more digits check)
    ];
    
    // Sort by length (longest first) to match most specific dial code first
    dialCodes.sort((a, b) => b.length - a.length);
    
    for (const dialCodeInfo of dialCodes) {
      if (digitsOnly.startsWith(dialCodeInfo.code)) {
        const digitsAfterDialCode = digitsOnly.length - dialCodeInfo.code.length;
        
        // For dial codes like +966, need at least 9 digits after (Saudi numbers are typically 9 digits)
        // For +20, need at least 8 digits (Egypt numbers are typically 8-10 digits)
        // For +1, need at least 10 digits
        const minDigitsAfter = dialCodeInfo.length === 1 ? 10 : 
                               dialCodeInfo.length === 2 ? 8 : 9;
        
        if (digitsAfterDialCode >= minDigitsAfter) {
          const nationalNumber = digitsOnly.substring(dialCodeInfo.code.length);
          
          try {
            const countryCallingCode = getCountryCallingCode(dialCodeInfo.country as CountryCode);
            const e164Format = `+${dialCodeInfo.code}${nationalNumber}`;
            
            const result: ParsedPhoneNumber = {
              isValid: false, // Mark as invalid since we couldn't validate format
              countryCode: `+${countryCallingCode}`,
              nationalNumber: nationalNumber,
              country: dialCodeInfo.country,
              internationalFormat: e164Format,
              e164Format: e164Format
            };
            
            console.log('📞 [PhoneUtil] Manual country detection successful:', result);
            return result;
          } catch (e) {
            // Failed to get country calling code, continue to next
          }
        }
      }
    }
    
    return null;
  } catch (error) {
    console.error('📞 [PhoneUtil] Error in manual country detection:', error);
    return null;
  }
}

/**
 * Test function to verify country code conflict handling
 */
export const testPhoneNumberParsing = () => {
  console.log('\n=== TESTING PHONE NUMBER PARSING ===\n');
  
  const testCases = [
    // Egypt (+20) vs hypothetical +201
    { number: '+201234567890', expected: { code: '+20', country: 'EG' } },
    { number: '+20 10 1234 5678', expected: { code: '+20', country: 'EG' } },
    
    // Saudi Arabia (+966) vs Kuwait (+965)
    { number: '+966501234567', expected: { code: '+966', country: 'SA' } },
    { number: '+965 1234 5678', expected: { code: '+965', country: 'KW' } },
    { number: '+96550123456', expected: { code: '+965', country: 'KW' } },
    
    // UAE (+971)
    { number: '+971501234567', expected: { code: '+971', country: 'AE' } },
    
    // Bahrain (+973) vs Qatar (+974)
    { number: '+97336123456', expected: { code: '+973', country: 'BH' } },
    { number: '+97450123456', expected: { code: '+974', country: 'QA' } },
    
    // USA (+1)
    { number: '+12025551234', expected: { code: '+1', country: 'US' } },
  ];
  
  const results = testCases.map(test => {
    const parsed = parseInternationalPhoneNumber(test.number);
    const passed = parsed?.countryCode === test.expected.code && 
                   parsed?.country === test.expected.country;
    
    console.log(`${passed ? '✅' : '❌'} ${test.number}`);
    console.log(`   Expected: ${test.expected.code} (${test.expected.country})`);
    console.log(`   Got: ${parsed?.countryCode || 'N/A'} (${parsed?.country || 'N/A'})`);
    if (parsed) {
      console.log(`   National: ${parsed.nationalNumber}`);
      console.log(`   E.164: ${parsed.e164Format}`);
    }
    console.log('');
    
    return { test, parsed, passed };
  });
  
  const passedCount = results.filter(r => r.passed).length;
  console.log(`\n=== RESULTS: ${passedCount}/${testCases.length} PASSED ===\n`);
  
  return results;
};

/**
 * Format phone number for display
 */
export const formatPhoneNumberForDisplay = (phoneNumber: string): string => {
  const parsed = parseInternationalPhoneNumber(phoneNumber);
  return parsed?.internationalFormat || phoneNumber;
};

/**
 * Get country code from phone number
 */
export const getCountryCodeFromPhone = (phoneNumber: string): string => {
  const parsed = parseInternationalPhoneNumber(phoneNumber);
  return parsed?.countryCode || '+1';
};

/**
 * Get national number from phone number
 */
export const getNationalNumberFromPhone = (phoneNumber: string): string => {
  const parsed = parseInternationalPhoneNumber(phoneNumber);
  return parsed?.nationalNumber || phoneNumber.replace(/^\+/, '');
};

/**
 * Get country ISO code from phone number
 */
export const getCountryFromPhone = (phoneNumber: string): CountryCode | undefined => {
  const parsed = parseInternationalPhoneNumber(phoneNumber);
  return parsed?.country as CountryCode | undefined;
};

